import type { ClienteApi } from './cliente';
import { claveIdempotencia, negocio } from './comun';
import { operacionesActividad } from './ops/actividad';
import { operacionesAnalitica } from './ops/analitica';
import { operacionesAsistente } from './ops/asistente';
import { operacionesBackoffice } from './ops/backoffice';
import { operacionesCanales } from './ops/canales';
import { operacionesCitas } from './ops/citas';
import { operacionesBuzon } from './ops/buzon';
import { operacionesCampanas } from './ops/campanas';
import { operacionesClavesApi } from './ops/clavesApi';
import { operacionesClientes } from './ops/clientes';
import { operacionesClientesCatalogo } from './ops/clientesCatalogo';
import { operacionesClientesDuplicados } from './ops/clientesDuplicados';
import { operacionesClientesImportar } from './ops/clientesImportar';
import { operacionesClientesPrivacidad } from './ops/clientesPrivacidad';
import { operacionesCuenta } from './ops/cuenta';
import { operacionesDisponibilidad } from './ops/disponibilidad';
import { operacionesEntregas } from './ops/entregas';
import { operacionesEquipo } from './ops/equipo';
import { operacionesIndicadores } from './ops/indicadores';
import { operacionesEventos } from './ops/eventos';
import { operacionesInvitacion } from './ops/invitacion';
import { operacionesListas } from './ops/listas';
import { operacionesNegocio } from './ops/negocio';
import { operacionesPagos } from './ops/pagos';
import { operacionesPartners } from './ops/partners';
import { operacionesPases } from './ops/pases';
import { operacionesPlan } from './ops/plan';
import { operacionesReservas } from './ops/reservas';
import { operacionesPedidos } from './ops/pedidos';
import { operacionesPortalRrpp } from './ops/portalRrpp';
import { operacionesServicios } from './ops/servicios';
import { operacionesReservasGestion } from './ops/reservasGestion';
import { operacionesSala } from './ops/sala';
import { operacionesTablet } from './ops/tablet';
import { operacionesWeb } from './ops/web';
import type { components as Entregas, operations as OperacionesEntregas } from './tipos/entregas';
import type { components as Plataforma, operations as OperacionesPlataforma } from './tipos/plataforma';

/**
 * Operaciones de la API v1 que usa el panel, con los tipos generados del contrato (`plataforma.yaml` y, para la campana,
 * `entregas.yaml`). Cada función nombra su `operationId`: si el contrato cambia, los tipos dejan de cuadrar y la
 * comprobación de tipos falla. Aquí, las del marco y el acceso (Paso 0); las de cada pantalla, en `ops/<pantalla>.ts`, que
 * se juntan en `crearOperaciones`.
 */
export { claveIdempotencia, consulta, negocio, type Pagina } from './comun';

type Esquema<N extends keyof Plataforma['schemas']> = Plataforma['schemas'][N];

export type Yo = Esquema<'Me'>;
export type Membresia = Yo['memberships'][number];
export type Navegacion = Esquema<'Navigation'>;
export type EntradaNavegacion = Navegacion['items'][number];
export type ZonaNavegacion = Esquema<'NavigationArea'>;
export type GrupoNavegacion = Esquema<'NavigationGroup'>;
export type AnidamientoNavegacion = Esquema<'NavigationNesting'>;
export type AccesoDeInicio = Esquema<'NavigationShortcut'>;
export type FranjaCerrada = Esquema<'DismissedNotice'>;
export type PreferenciasMenu = Esquema<'NavigationPreferences'>;
export type AccesoEnElMenu = Esquema<'NavigationPlatformAccess'>;
export type Negocio = Esquema<'Business'>;
export type ListaMiembros = Esquema<'MemberList'>;
export type ListaInvitaciones = Esquema<'InvitationList'>;
export type ListaTablets = Esquema<'DeviceList'>;
export type ListaFacturas = Esquema<'InvoiceList'>;
export type ListaRoles = OperacionesPlataforma['listRoles']['responses'][200]['content']['application/json'];
export type Rol = Esquema<'Role'>;
export type PermisoEfectivo = Esquema<'EffectivePermission'>;
export type Bloques = Esquema<'Entitlements'>;
export type ModoSuscripcion = Esquema<'SubscriptionMode'>;
export type EstadoSuscripcion = Esquema<'SubscriptionStatus'>;
export type ParDeTokens = Esquema<'TokenPair'>;
export type RetoDosPasos = Esquema<'TwoFactorChallenge'>;
export type Credenciales = Esquema<'Credentials'>;
export type RespuestaDosPasos = Esquema<'TwoFactorAnswer'>;
export type IdNegocio = Esquema<'BusinessId'>;
export type Suscripcion = Esquema<'Subscription'>;
export type Contador = Esquema<'UsageCounter'>;
export type AccesoPlataforma = Esquema<'PlatformAccess'>;
export type RespuestaDosPasosEnElMomento = Esquema<'SessionTwoFactorAnswer'>;
export type ConfirmacionDosPasos = Esquema<'SessionTwoFactorConfirmation'>;
export type NuevaContrasena = OperacionesPlataforma['confirmPasswordReset']['requestBody']['content']['application/json'];

export type ListaAvisos = Entregas['schemas']['MyNoticeList'];
export type Aviso = Entregas['schemas']['MyNotice'];
export type ResumenAvisos = Entregas['schemas']['NoticeSummary'];
export type Gravedad = Entregas['schemas']['NoticeSeverity'];
type ConsultaAvisos = NonNullable<OperacionesEntregas['listMyNotices']['parameters']['query']>;
type MarcadosTodos = OperacionesEntregas['markAllNoticesRead']['responses'][200]['content']['application/json'];



/** ¿Es un reto de dos pasos (y no ya la sesión)? */
export function esRetoDosPasos(respuesta: ParDeTokens | RetoDosPasos): respuesta is RetoDosPasos {
    return 'challenge_token' in respuesta;
}

export function crearOperaciones(api: ClienteApi) {
    return {
        ...operacionesCuenta(api),
        ...operacionesEquipo(api),
        ...operacionesInvitacion(api),
        ...operacionesTablet(api),
        ...operacionesNegocio(api),
        ...operacionesPlan(api),
        ...operacionesActividad(api),
        // Agenda y Disponibilidad (3.1 a 3.4): «Horario» y «Cuándo se puede reservar» de Negocio.
        ...operacionesDisponibilidad(api),
        // Apartados de Clientes 2.5 a 2.7: sus lecturas de la ficha tienen la forma de las de 2.1–2.4, que van después.
        ...operacionesClientesDuplicados(api),
        ...operacionesClientesImportar(api),
        ...operacionesClientesPrivacidad(api),
        ...operacionesClientes(api),
        // 2.8 después: su listTags y su listCustomFields admiten además `include_archived`.
        ...operacionesClientesCatalogo(api),
        // Pagos (4.1 a 4.8), con lo que sus pantallas leen de Reservas, Citas y Clientes.
        ...operacionesPagos(api),
        // Citas (9.1 a 9.3 y 9.8): Agenda, Nueva cita, ficha y Mi agenda; después de Pagos, que usa tres de ellas con menos parámetros.
        ...operacionesCitas(api),
        // Citas: «Servicios y profesionales» (9.4, el catálogo con su ficha) y las plantillas de consentimiento.
        ...operacionesServicios(api),
        // Reservas (8.1 Reservas del día y 8.4 Por completar): su listReservations, con todos los filtros y `counts`, va después de la de Pagos.
        ...operacionesReservas(api),
        // Reservas (8.2 Nueva reserva, 8.3 ficha y 8.5 Zonas y recursos) y la Sala en vivo (8.7, sala.yaml).
        ...operacionesReservasGestion(api),
        ...operacionesSala(api),
        // Entregas y Notificaciones (5.1 a 5.5): «Avisos» y «Mensajes»; la campana, más abajo.
        ...operacionesEntregas(api),
        // Web pública (13.1 a 13.5): «Tu web», «Widgets» y «Medición».
        ...operacionesWeb(api),
        // Claves de API (13.6, G633), en «Para desarrolladores» de Widgets.
        ...operacionesClavesApi(api),
        // Asistente IA (11.5 a 11.7), con lo que lee de Canales.
        ...operacionesAsistente(api),
        // Canales (11.1 Canales, 11.2 Conectar un canal y 11.4 Plantillas de Meta).
        ...operacionesCanales(api),
        // Buzón (11.3) y su apartado Llamadas (11.9, próximamente).
        ...operacionesBuzon(api),
        // Campañas y mensajes automáticos (12.1 a 12.7).
        ...operacionesCampanas(api),
        // Indicadores (7.2) y Exportaciones (7.3).
        ...operacionesIndicadores(api),
        ...operacionesAnalitica(api),
        // Pases y Acceso: la Puerta del panel (6.3 Preparar, 6.4 Conflictos, 6.5 Registro).
        ...operacionesPases(api),
        // Eventos (10.1 a 10.5), Pedidos, Informes y Taquilla (10.6 a 10.8) y el portal del RRPP (10.12).
        ...operacionesEventos(api),
        ...operacionesPedidos(api),
        ...operacionesPortalRrpp(api),
        // Listas, RRPP y Liquidaciones (10.9 a 10.11).
        ...operacionesListas(api),
        // Portal de partners (1.23), fuera de un negocio, y su solicitud pública.
        ...operacionesPartners(api),
        // Backoffice de plataforma (1.20): negocios, su ficha y los accesos de Soporte y Superadmin, fuera de un negocio.
        ...operacionesBackoffice(api),
        // ---- Sesión (plataforma) ----
        /** createSession: email y contraseña; devuelve la sesión o el reto de dos pasos. */
        createSession: (credenciales: Credenciales, clave = claveIdempotencia()) =>
            api.pedir<ParDeTokens | RetoDosPasos>('POST', '/v1/auth/sessions', { cuerpo: credenciales, idempotencia: clave }),
        /** completeTwoFactorChallenge: código de la aplicación o código de recuperación. */
        completeTwoFactorChallenge: (respuesta: RespuestaDosPasos, clave = claveIdempotencia()) =>
            api.pedir<ParDeTokens>('POST', '/v1/auth/sessions/two-factor', { cuerpo: respuesta, idempotencia: clave }),
        /** selectBusiness: fija el negocio en la sesión y devuelve los tokens nuevos. */
        selectBusiness: (businessId: IdNegocio, clave = claveIdempotencia()) =>
            api.pedir<ParDeTokens>('POST', '/v1/auth/sessions/current/business', { cuerpo: { business_id: businessId }, idempotencia: clave }),
        /**
         * refreshSession: tokens nuevos con el de refresco, que rota en cada uso (reutilizar uno ya usado revoca la
         * familia). Sin el token de acceso, que es el que ha caducado.
         */
        refreshSession: (refreshToken: string) =>
            api.pedir<ParDeTokens>('POST', '/v1/auth/sessions/refresh', { cuerpo: { refresh_token: refreshToken }, sinSesion: true }),
        /** sendEmailVerification: enlace nuevo (24 h, G134); con un cambio pendiente va al email nuevo (G211). */
        sendEmailVerification: (clave = claveIdempotencia()) => api.pedir<undefined>('POST', '/v1/auth/email-verifications', { idempotencia: clave }),
        /** confirmEmailVerification: el token del enlace del email; también confirma un cambio de email pendiente (G172). */
        confirmEmailVerification: (token: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('POST', '/v1/auth/email-verifications/confirm', { cuerpo: { token }, idempotencia: clave, sinSesion: true }),
        /** deleteCurrentSession: cerrar sesión. */
        deleteCurrentSession: (clave = claveIdempotencia()) => api.pedir<undefined>('DELETE', '/v1/auth/sessions/current', { idempotencia: clave }),
        /** requestPasswordReset: 202 aunque la cuenta no exista (no se revela). */
        requestPasswordReset: (email: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('POST', '/v1/auth/password-resets', { cuerpo: { email }, idempotencia: clave }),
        /** confirmPasswordReset: el token del enlace del email y la contraseña nueva. */
        confirmPasswordReset: (datos: NuevaContrasena, clave = claveIdempotencia()) =>
            api.pedir<undefined>('POST', '/v1/auth/password-resets/confirm', { cuerpo: datos, idempotencia: clave }),
        /**
         * confirmSessionTwoFactor: el código de dos pasos «en el momento» (G116), cuando una operación con
         * `x-two-factor: session` responde `401 two_factor_confirmation_required`. Vale 10 minutos en esta sesión.
         */
        confirmSessionTwoFactor: (respuesta: RespuestaDosPasosEnElMomento, clave = claveIdempotencia()) =>
            api.pedir<ConfirmacionDosPasos>('POST', '/v1/me/sessions/current/two-factor-confirmation', { cuerpo: respuesta, idempotencia: clave }),

        // ---- Cuenta, menú y bloques (plataforma) ----
        /** getMe: la cuenta y sus negocios, cada uno con su slug y el estado de su suscripción (G99). */
        getMe: () => api.pedir<Yo>('GET', '/v1/me'),
        /**
         * startEmailChange: el email nuevo queda pendiente (`pending_email`) hasta abrir el enlace que le llega (G172); antes
         * de la primera verificación, corrige el email sin más.
         */
        startEmailChange: (email: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('POST', '/v1/me/email-change', { cuerpo: { email }, idempotencia: clave }),
        /**
         * getMyNavigation: menú y perfil (ya filtrados, con zona, categoría, icono y orden, G97, G128), con las preferencias
         * de la persona ya aplicadas (G129), las entradas que van dentro de otra (G130), el acceso de plataforma abierto
         * (G137) y los permisos efectivos (G98).
         */
        getMyNavigation: (businessId: IdNegocio) => api.pedir<Navegacion>('GET', `${negocio(businessId)}/me/navigation`),
        /** getMyNavigationPreferences: lo guardado por la persona en este negocio («Restablecer» apagado si no hay nada, G129). */
        getMyNavigationPreferences: (businessId: IdNegocio) => api.pedir<PreferenciasMenu>('GET', `${negocio(businessId)}/me/navigation-preferences`),
        /** updateMyNavigationPreferences: solo cambian los campos que se mandan; `null` vuelve a lo de por defecto (G129). */
        updateMyNavigationPreferences: (businessId: IdNegocio, cambios: PreferenciasMenu, clave = claveIdempotencia()) =>
            api.pedir<PreferenciasMenu>('PATCH', `${negocio(businessId)}/me/navigation-preferences`, { cuerpo: cambios, idempotencia: clave }),
        /** getBusiness (`negocio.ver`): el nombre del negocio cuando quien mira no es miembro (acceso de plataforma, G137). */
        getBusiness: (businessId: IdNegocio) => api.pedir<Negocio>('GET', negocio(businessId)),
        /** listEntitlements: modo (operativo o solo lectura), estado de la suscripción y bloques vigentes. */
        listEntitlements: (businessId: IdNegocio) => api.pedir<Bloques>('GET', `${negocio(businessId)}/entitlements`),
        /** getSubscription (`plan.ver`): fechas de la prueba, de la gracia del impago y de la baja, para las franjas (0.5). */
        getSubscription: (businessId: IdNegocio) => api.pedir<Suscripcion>('GET', `${negocio(businessId)}/subscription`),
        /** getUsage (`plan.ver`): contadores del periodo, para la franja del cupo al 90 % (0.5). */
        getUsage: (businessId: IdNegocio) => api.pedir<{ data: Contador[] }>('GET', `${negocio(businessId)}/usage`),

        // ---- Acceso de plataforma (Soporte o Superadmin dentro de un negocio, G68 a G70) ----
        /** closePlatformAccess: «Salir» de la franja índigo (0.5), con el `id` de `platform_access` de getMyNavigation (G137). */
        closePlatformAccess: (accessId: string, clave = claveIdempotencia()) =>
            api.pedir<AccesoPlataforma>('POST', `/v1/platform/accesses/${encodeURIComponent(accessId)}/close`, { idempotencia: clave }),

        // ---- Campana (entregas) ----
        /** listMyNotices: avisos del miembro (sin los archivados si se pide `state`). */
        listMyNotices: (businessId: IdNegocio, consulta: ConsultaAvisos = {}) => {
            const parametros = new URLSearchParams();
            for (const [clave, valor] of Object.entries(consulta) as [string, string | number | boolean | undefined][]) {
                if (valor !== undefined) {
                    parametros.set(clave, String(valor));
                }
            }
            const query = parametros.toString();
            return api.pedir<ListaAvisos>('GET', `${negocio(businessId)}/notices${query === '' ? '' : `?${query}`}`);
        },
        /** getMyNoticeSummary: cuántos sin leer, por gravedad. */
        getMyNoticeSummary: (businessId: IdNegocio) => api.pedir<ResumenAvisos>('GET', `${negocio(businessId)}/notices/summary`),
        /** markNoticeRead. */
        markNoticeRead: (businessId: IdNegocio, noticeId: string, clave = claveIdempotencia()) =>
            api.pedir<Aviso>('POST', `${negocio(businessId)}/notices/${encodeURIComponent(noticeId)}/read`, { idempotencia: clave }),
        /** markAllNoticesRead. */
        markAllNoticesRead: (businessId: IdNegocio, clave = claveIdempotencia()) =>
            api.pedir<MarcadosTodos>('POST', `${negocio(businessId)}/notices/read-all`, { idempotencia: clave }),
        /** archiveNotice. */
        archiveNotice: (businessId: IdNegocio, noticeId: string, clave = claveIdempotencia()) =>
            api.pedir<Aviso>('POST', `${negocio(businessId)}/notices/${encodeURIComponent(noticeId)}/archive`, { idempotencia: clave }),
    };
}

export type Operaciones = ReturnType<typeof crearOperaciones>;
